import { useEffect, useState } from "react";
import { CopyButton, ErrorBox, Field, Loading, Toggle, useAction, useNow, useToast } from "../../components/ui.tsx";
import { post, useApi } from "../../lib/api.ts";
import { ago, duration } from "../../lib/format.ts";
import { useSettings } from "../../lib/settings.ts";
import type { BotDetails } from "../../lib/types.ts";

const STATE_TEXT = { disabled: "Not configured", connecting: "Connecting…", ready: "Connected", error: "Error" } as const;
const STATE_TONE = { disabled: "", connecting: "warn", ready: "ok", error: "bad" } as const;

export function useBot(fast = false) {
  return useApi<BotDetails>("/api/discord", { refreshInterval: (d) => (fast || d?.state === "connecting" ? 2000 : 15_000) });
}

export function BotStatusCard({ bot }: { bot: BotDetails }) {
  const now = useNow(30_000);
  const [reconnect, reconnecting] = useAction(() => post("/api/discord/reconnect"), "Reconnecting…");
  const [register, registering] = useAction(() => post("/api/discord/commands"), "Slash commands registered");
  return (
    <div className={`callout ${bot.state === "ready" ? "ok" : bot.state === "error" ? "bad" : "warn"}`}>
      <div className="row between">
        <div className="stack tight">
          <div className="row">
            <i className={`dot ${STATE_TONE[bot.state]}`} />
            <b>{STATE_TEXT[bot.state]}</b>
            {bot.user && <span>as {bot.user}</span>}
            {bot.guild && <span className="muted">· in {bot.guild.name}</span>}
            {bot.readyAt && <span className="muted">· up {duration(now - Date.parse(bot.readyAt))}</span>}
          </div>
          {bot.state === "ready" && (
            <div className="muted small">
              {bot.commandsRegisteredAt ? `Slash commands registered ${ago(bot.commandsRegisteredAt, now)}` : "Slash commands not registered (pick a server)"}
              {bot.latencyMs !== undefined && ` · gateway latency ${bot.latencyMs} ms`}
              {` · in ${bot.guilds.length} server${bot.guilds.length === 1 ? "" : "s"}`}
            </div>
          )}
          {bot.error && <div className="small">{bot.error}</div>}
        </div>
        <div className="row">
          {bot.state !== "disabled" && (
            <button className="btn sm" disabled={reconnecting} onClick={() => reconnect()}>
              Reconnect
            </button>
          )}
          {bot.state === "ready" && bot.guild && (
            <button className="btn sm" disabled={registering} onClick={() => register()}>
              Re-register slash commands
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Token + application ID, then the invite link. Used by the wizard and the settings page. */
export function DiscordCredentials({ bot, onSaved }: { bot?: BotDetails; onSaved?: () => void }) {
  const settings = useSettings();
  const [token, setToken] = useState("");
  const [appId, setAppId] = useState<string | null>(null);
  const tokenSet = settings.get<{ set: boolean }>("discord.token", { set: false }).set;
  const effectiveAppId = appId ?? settings.get<string>("discord.app_id", "");
  const [save, saving] = useAction(async () => {
    const values: Record<string, unknown> = { "discord.app_id": effectiveAppId.trim() || null };
    if (token.trim()) values["discord.token"] = token.trim();
    await settings.save(values);
    setToken("");
    onSaved?.();
  }, "Saved. The bot restarts with the new settings.");

  return (
    <div className="stack">
      <div className="grid2">
        <Field label="Bot token" hint={tokenSet ? "A token is saved (encrypted). Paste a new one to replace it." : "Developer portal → your app → Bot → Reset Token"}>
          <input type="password" autoComplete="off" className="mono" value={token} placeholder={tokenSet ? "•••••••••••••••• (saved)" : "paste the bot token"} onChange={(e) => setToken(e.target.value)} />
        </Field>
        <Field label="Application ID" hint="General Information → Application ID">
          <input className="mono" value={effectiveAppId} onChange={(e) => setAppId(e.target.value)} placeholder="1428811930025562412" />
        </Field>
      </div>
      <div className="callout small">
        In the developer portal, turn on <b>Message Content Intent</b> (Bot → Privileged Gateway Intents), or the bot cannot read thread messages.
      </div>
      <div className="row">
        <button className="btn primary" disabled={saving || (!token.trim() && appId === null)} onClick={() => save()}>
          {saving ? "Saving…" : tokenSet ? "Save" : "Save and connect"}
        </button>
        <a className="btn" href="https://discord.com/developers/applications" target="_blank" rel="noreferrer">
          Open developer portal ↗
        </a>
      </div>
      {bot?.inviteUrl && (
        <Field label="Invite link" hint="Open it to add the bot to your server with the permissions it needs.">
          <div className="row nowrap">
            <input readOnly className="mono" value={bot.inviteUrl} onFocus={(e) => e.target.select()} />
            <CopyButton text={bot.inviteUrl} />
            <a className="btn sm" href={bot.inviteUrl} target="_blank" rel="noreferrer">
              Open ↗
            </a>
          </div>
        </Field>
      )}
    </div>
  );
}

export function ServerPicker({ bot }: { bot: BotDetails }) {
  const settings = useSettings();
  const current = settings.get<string>("discord.guild_id", "");
  const [manual, setManual] = useState("");
  const [save, saving] = useAction((id: string) => settings.save({ "discord.guild_id": id || null }), "Server saved. Registering commands…");
  if (bot.state !== "ready" && !current) return <div className="muted small">Connect the bot first; its servers show up here.</div>;
  return (
    <Field label="Server" hint={bot.guilds.length ? "The bot registers slash commands and creates project channels here." : "The bot is not in any server yet. Use the invite link above."}>
      {bot.guilds.length ? (
        <select value={current} disabled={saving} onChange={(e) => save(e.target.value)}>
          <option value="">Pick a server…</option>
          {bot.guilds.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
          {current && !bot.guilds.some((g) => g.id === current) && <option value={current}>{current} (bot not in it)</option>}
        </select>
      ) : (
        <div className="row nowrap">
          <input className="mono" placeholder="server ID" value={manual || current} onChange={(e) => setManual(e.target.value)} />
          <button className="btn" disabled={saving || !manual} onClick={() => save(manual.trim())}>
            Save
          </button>
        </div>
      )}
    </Field>
  );
}

function PairingCode() {
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const now = useNow(1000);
  const [mint, minting] = useAction(async () => setCode(await post<{ code: string; expiresAt: string }>("/api/pairing-codes")));
  const left = code ? Date.parse(code.expiresAt) - now : 0;
  return (
    <div className="stack tight">
      {code && left > 0 ? (
        <div className="callout">
          <div className="row between">
            <div>
              Send <code>/pair {code.code}</code> in any channel of your server.
              <div className="muted small">Expires in {duration(left)}. Single use.</div>
            </div>
            <div className="code6">{code.code}</div>
          </div>
        </div>
      ) : (
        <div className="muted small">Pair a Discord user: mint a code, then they send /pair &lt;code&gt;. No developer mode or copying IDs.</div>
      )}
      <div>
        <button className="btn" disabled={minting} onClick={() => mint()}>
          {code && left > 0 ? "New code" : "New pairing code"}
        </button>
      </div>
    </div>
  );
}

export function DiscordSettings() {
  const bot = useBot();
  const settings = useSettings();
  const toast = useToast();
  const [category, setCategory] = useState<string | null>(null);
  const [logChannel, setLogChannel] = useState<string | null>(null);
  const [manualUser, setManualUser] = useState("");
  useEffect(() => {
    setCategory(null);
    setLogChannel(null);
  }, [settings.data]);

  const [saveChannels, savingChannels] = useAction(
    () =>
      settings.save({
        "discord.category": (category ?? settings.get("discord.category", "claude-code")).trim() || "claude-code",
        "discord.log_channel_id": (logChannel ?? settings.get("discord.log_channel_id", "")) || null,
      }),
    "Saved",
  );
  const [test, testing] = useAction(() => post("/api/discord/test", { channelId: (logChannel ?? settings.get("discord.log_channel_id", "")) || undefined }), "Test message sent");
  const allowed = settings.get<string[]>("discord.allowed_users", []);
  const setAllowed = (ids: string[]) => settings.save({ "discord.allowed_users": ids }).catch((e) => toast(String(e.message ?? e), "bad"));

  if (!bot.data || !settings.data) return bot.error || settings.error ? <ErrorBox error={bot.error ?? settings.error} /> : <Loading />;
  const b = bot.data;
  const notify = [
    ["notify.finished", "finishes"],
    ["notify.question", "asks a question"],
    ["notify.permission", "needs permission"],
    ["notify.error", "errors or ends a turn with an error"],
    ["notify.started", "starts"],
  ] as const;
  const names = new Map(b.allowedUsers.map((u) => [u.id, u.name]));

  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Discord</h1>
          <div className="sub">Everything the bot needs, without a terminal. Saving restarts the bot; sessions keep running.</div>
        </div>
      </div>
      <BotStatusCard bot={b} />

      <section className="card">
        <h2>Connection</h2>
        <DiscordCredentials bot={b} />
      </section>

      <section className="card">
        <h2>Server and channels</h2>
        <ServerPicker bot={b} />
        <div className="grid2">
          <Field label="Category for project channels" hint="Created if missing. One channel per project goes under it.">
            <input value={category ?? settings.get("discord.category", "claude-code")} onChange={(e) => setCategory(e.target.value)} />
          </Field>
          <Field label="Log channel" hint="One line per lifecycle event across all projects. Optional.">
            <select value={logChannel ?? settings.get("discord.log_channel_id", "")} onChange={(e) => setLogChannel(e.target.value)}>
              <option value="">None</option>
              {b.channels.map((c) => (
                <option key={c.id} value={c.id}>
                  #{c.name}
                  {c.category ? ` (${c.category})` : ""}
                </option>
              ))}
              {settings.get("discord.log_channel_id", "") && !b.channels.some((c) => c.id === settings.get("discord.log_channel_id", "")) && (
                <option value={settings.get("discord.log_channel_id", "")}>{settings.get("discord.log_channel_id", "")}</option>
              )}
            </select>
          </Field>
        </div>
        <div className="row">
          <button className="btn primary" disabled={savingChannels || (category === null && logChannel === null)} onClick={() => saveChannels()}>
            Save
          </button>
          <button className="btn" disabled={testing || b.state !== "ready" || !(logChannel ?? settings.get("discord.log_channel_id", ""))} onClick={() => test()}>
            Send a test message
          </button>
        </div>
      </section>

      <section className="card">
        <header>
          <h2>Allowed users</h2>
          <span className="muted small">Only these users can run commands, press buttons or talk to sessions.</span>
        </header>
        {allowed.length ? (
          <div className="list card flush">
            {allowed.map((id) => (
              <div key={id} className="row between">
                <span>
                  <b>{names.get(id) ?? "Unknown user"}</b> <span className="muted mono small">{id}</span>
                </span>
                <button className="btn sm danger" onClick={() => setAllowed(allowed.filter((x) => x !== id))}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="callout warn">Nobody is allowed yet, so the bot ignores everyone. Pair yourself below.</div>
        )}
        <PairingCode />
        <details>
          <summary className="muted small">Add a user ID by hand</summary>
          <div className="row nowrap" style={{ marginTop: 8 }}>
            <input className="mono" placeholder="Discord user ID" value={manualUser} onChange={(e) => setManualUser(e.target.value.replace(/\D/g, ""))} />
            <button
              className="btn"
              disabled={!manualUser}
              onClick={() => {
                void setAllowed([...new Set([...allowed, manualUser])]);
                setManualUser("");
              }}
            >
              Add
            </button>
          </div>
        </details>
      </section>

      <section className="card">
        <h2>Mention me when a session…</h2>
        <div className="stack">
          {notify.map(([key, label]) => (
            <Toggle key={key} label={label} checked={settings.get<boolean>(key, false)} onChange={(v) => void settings.save({ [key]: v })} />
          ))}
        </div>
        <div className="muted small">Mentions go to every allowed user.</div>
      </section>
    </>
  );
}
